import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ThemeProvider } from "@/context/ThemeProvider";
import { ThemeRouteWatcher } from "@/components/ThemeRouteWatcher";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import SignupPatient from "@/pages/SignupPatient";
import SignupHospital from "@/pages/SignupHospital";
import Stub from "@/pages/Stub";

import HospitalLayout from "@/pages/hospital/HospitalLayout";
import Overview from "@/pages/hospital/Overview";
import Doctors from "@/pages/hospital/Doctors";
import Schedules from "@/pages/hospital/Schedules";
import Settings from "@/pages/hospital/Settings";
import HospitalAppointments from "@/pages/hospital/Appointments";
import HospitalReports from "@/pages/hospital/Reports";

import PatientLayout from "@/pages/patient/PatientLayout";
import PatientHome from "@/pages/patient/PatientHome";
import PatientHospitals from "@/pages/patient/Hospitals";
import HospitalDetail from "@/pages/patient/HospitalDetail";
import DoctorDetail from "@/pages/patient/DoctorDetail";
import PatientAppointments from "@/pages/patient/PatientAppointments";
import StripeCheckout from "@/pages/patient/StripeCheckout";
import StripeConfirm from "@/pages/patient/StripeConfirm";
import PatientProfile from "@/pages/patient/Profile";

import DoctorLayout from "@/pages/doctor/DoctorLayout";
import DoctorToday from "@/pages/doctor/Today";
import DoctorAppointments from "@/pages/doctor/DoctorAppointments";
import DoctorAppointmentDetail from "@/pages/doctor/AppointmentDetail";
import DoctorPatients from "@/pages/doctor/Patients";
import DoctorPatientHistory from "@/pages/doctor/PatientHistory";
import DoctorBlocks from "@/pages/doctor/Blocks";
import DoctorProfilePage from "@/pages/doctor/DoctorProfile";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminHospitals from "@/pages/admin/AdminHospitals";
import AdminHospitalDetail from "@/pages/admin/AdminHospitalDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReports from "@/pages/admin/AdminReports";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import HospitalIntegrations from "@/pages/hospital/HospitalIntegrations";
import PaymentIntegration from "@/pages/hospital/PaymentIntegration";
import AdminPaymentProviders from "@/pages/admin/AdminPaymentProviders";
import Branding from "@/pages/hospital/Branding";

function App() {
  return (
    <div className="App min-h-screen bg-mb-bg text-mb-fg">
      <AuthProvider>
        <ThemeProvider>
        <BrowserRouter>
          <ThemeRouteWatcher />
          <Toaster theme="dark" position="top-right"
            toastOptions={{ style: { background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" } }} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup/patient" element={<SignupPatient />} />
            <Route path="/signup/hospital" element={<SignupHospital />} />

            <Route path="/admin" element={<ProtectedRoute allow={["super_admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="hospitals" element={<AdminHospitals />} />
              <Route path="hospitals/:id" element={<AdminHospitalDetail />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="payment-providers" element={<AdminPaymentProviders />} />
            </Route>

            <Route path="/doctor" element={<ProtectedRoute allow={["doctor"]}><DoctorLayout /></ProtectedRoute>}>
              <Route index element={<DoctorToday />} />
              <Route path="appointments" element={<DoctorAppointments />} />
              <Route path="appointments/:id" element={<DoctorAppointmentDetail />} />
              <Route path="patients" element={<DoctorPatients />} />
              <Route path="patients/:id" element={<DoctorPatientHistory />} />
              <Route path="blocks" element={<DoctorBlocks />} />
              <Route path="profile" element={<DoctorProfilePage />} />
            </Route>

            <Route path="/patient" element={<ProtectedRoute allow={["patient"]}><PatientLayout /></ProtectedRoute>}>
              <Route index element={<PatientHome />} />
              <Route path="hospitals" element={<PatientHospitals />} />
              <Route path="hospitals/:id" element={<HospitalDetail />} />
              <Route path="doctors/:id" element={<DoctorDetail />} />
              <Route path="appointments" element={<PatientAppointments />} />
              <Route path="appointments/:id/checkout" element={<StripeCheckout />} />
              <Route path="appointments/:id/confirm" element={<StripeConfirm />} />
              <Route path="profile" element={<PatientProfile />} />
            </Route>

            <Route path="/hospital" element={<ProtectedRoute allow={["hospital_admin"]}><HospitalLayout /></ProtectedRoute>}>
              <Route index element={<Overview />} />
              <Route path="doctors" element={<Doctors />} />
              <Route path="schedules" element={<Schedules />} />
              <Route path="appointments" element={<HospitalAppointments />} />
              <Route path="reports" element={<HospitalReports />} />
              <Route path="integrations" element={<HospitalIntegrations />} />
              <Route path="payments" element={<PaymentIntegration />} />
              <Route path="branding" element={<Branding />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
